import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import { cleanDateInput, dateOrNull, errorText, todayIso } from '../../utils/e2i';

export interface ClientDecisionInput { decision: 'approved' | 'rejected'; poRef: string | null; poDate: string | null; note: string | null }

interface Props {
  open: boolean;
  quotationText: string;
  onClose: () => void;
  onSave: (input: ClientDecisionInput) => Promise<void>;
}

/** What the client answered to a sent quotation: approved (with its PO) or rejected. */
const ClientDecisionDialog: React.FC<Props> = ({ open, quotationText, onClose, onSave }) => {
  const [decision, setDecision] = useState<'approved' | 'rejected'>('approved');
  const [poRef, setPoRef] = useState('');
  const [poDate, setPoDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDecision('approved');
    setPoRef('');
    setPoDate('');
    setNote('');
    setBusy(false);
    setError(null);
  }, [open]);

  const badDate = !!poDate.trim() && !dateOrNull(poDate);
  const blocked = badDate || (decision === 'rejected' && !note.trim());

  const save = async () => {
    if (blocked) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({
        decision,
        poRef: decision === 'approved' ? poRef.trim() || null : null,
        poDate: decision === 'approved' ? dateOrNull(poDate) : null,
        note: note.trim() || null,
      });
    } catch (e: any) {
      setError(errorText(e, 'The decision could not be recorded'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="e2i-client-title">
      <DialogTitle id="e2i-client-title">Client decision on {quotationText}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RadioGroup row aria-label="The client's decision" value={decision} onChange={(e) => setDecision(e.target.value as 'approved' | 'rejected')}>
              <FormControlLabel value="approved" control={<Radio size="small" />} label="Approved by the client" />
              <FormControlLabel value="rejected" control={<Radio size="small" />} label="Rejected by the client" />
            </RadioGroup>
            <Typography variant="body2" color="text.secondary">
              {decision === 'approved'
                ? 'The enquiry gets register status 12 (Quote approved) and the job can be created from this quotation.'
                : 'The enquiry stays open (register status 10): the quotation can be revised, or the enquiry cancelled.'}
            </Typography>
          </Grid>
          {decision === 'approved' ? (
            <>
              <Grid item xs={12} sm={7}>
                <TextField id="e2i-client-po" label="Client PO number" value={poRef} onChange={(e) => setPoRef(e.target.value.slice(0, 100))} fullWidth helperText="Printed on the sales order and the invoices as the customer's reference" />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  id="e2i-client-po-date"
                  type="date"
                  label="PO date"
                  value={poDate}
                  onChange={(e) => setPoDate(cleanDateInput(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: todayIso() }}
                  error={badDate}
                  helperText={badDate ? 'Not a date from 2000 to 2099' : undefined}
                  fullWidth
                />
              </Grid>
            </>
          ) : null}
          <Grid item xs={12}>
            <TextField
              id="e2i-client-note"
              label={decision === 'rejected' ? 'Why the client rejected it (required)' : 'Note (optional)'}
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 2000))}
              multiline
              minRows={2}
              fullWidth
              required={decision === 'rejected'}
            />
          </Grid>
        </Grid>
        {error ? <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Back</Button>
        <Button color={decision === 'rejected' ? 'error' : 'primary'} onClick={() => { void save(); }} disabled={busy || blocked}>
          {busy ? 'Saving…' : decision === 'approved' ? 'Record approval' : 'Record rejection'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ClientDecisionDialog;
